import { useEffect, useRef, useState } from 'react';
import { SATIS_FIYATI as T } from '../metin/satisFiyati';
import { hizliTeklifApi, type PiyasaFiyatiCevabi } from './istemci';
import { bookingCekilebilir, bookingOdaIstegi, konukMetni, paraYaz } from './mantik';
import type { OdaDuzeni } from './tipler';

const BEKLEME_MS = 8_000;
const EN_COK_DENEME = 12;

type PiyasaDurumu = { readonly tur: 'bos' } | { readonly tur: 'cekiliyor' } | { readonly tur: 'bitti'; readonly cevap: Exclude<PiyasaFiyatiCevabi, { durum: 'BEKLIYOR' }> } | { readonly tur: 'hata'; readonly metin: string };

function farkMetni(fark: number, para: string): string {
  if (fark === 0) return T.farkYok;
  return fark > 0 ? T.farkYuksek(paraYaz(fark, para)) : T.farkDusuk(paraYaz(-fark, para));
}

/**
 * Bir otelin teklife özel satış fiyatı ve yanında Booking fiyatı (02.11.2026).
 *
 * Satışçı sistemin %10'lu fiyatını Booking'e bakıp değiştirebilir (ör. 1.650 yerine 1.850 USD). Booking fiyatı
 * yalnız satışçıya gösterilen bilgidir; teklife, PDF'e ve mesaja GİRMEZ ve elle yazılana otomatik uygulanmaz.
 */
export function SatisFiyatiAlani({ onek, otelAdi, slug, sistemMinor, para, deger, degistir, gecerliMinor, hatali, saltOkunur, giris, cikis, odalar, odaAdlari }: {
  onek: string;
  otelAdi: string;
  slug: string | null;
  /** Düzenleme uygulanmamış sistem toplamı; oda seçimi eksikse null. */
  sistemMinor: number | null;
  para: string | null;
  deger: string;
  degistir: (v: string) => void;
  /** Teklife uygulanan fiyat; yoksa null. */
  gecerliMinor: number | null;
  hatali: boolean;
  saltOkunur: boolean;
  giris: string;
  cikis: string;
  odalar: readonly OdaDuzeni[];
  /** Oda sırasına göre teklifte seçilen oda tipi adları (Booking'de o odayı eşleştirmek için); seçimi olmayan null. */
  odaAdlari?: readonly (string | null)[];
}) {
  const [piyasa, setPiyasa] = useState<PiyasaDurumu>({ tur: 'bos' });
  const iptal = useRef<AbortController | null>(null);
  useEffect(() => () => iptal.current?.abort(), []);

  const tekOda = odalar.length === 1 ? odalar[0] : undefined;
  const cekilebilir = bookingCekilebilir({ saltOkunur, slug, odaSayisi: odalar.length });
  const cokOda = odalar.length > 1;

  const cek = async () => {
    if (slug === null || odalar.length === 0) return;
    iptal.current?.abort();
    const c = new AbortController();
    iptal.current = c;
    setPiyasa({ tur: 'cekiliyor' });
    try {
      // Çok odada her oda Booking'de sırayla sorulur (taze sorgu 46-87 sn ölçüldü): deneme sayısı oda sayısıyla çarpılır.
      for (let deneme = 0; deneme < EN_COK_DENEME * odalar.length; deneme += 1) {
        const cevap = await hizliTeklifApi.piyasaFiyati(
          tekOda !== undefined
            ? { slug, girisTarihi: giris, cikisTarihi: cikis, yetiskin: tekOda.yetiskin, ...(tekOda.cocukYaslari.length === 0 ? {} : { cocukYaslari: tekOda.cocukYaslari }) }
            // Çok odalı: her oda kendi doluluğu ve seçilen oda adıyla; üst düzey yetişkin toplamı yalnız şema zorunluluğu.
            : { slug, girisTarihi: giris, cikisTarihi: cikis, yetiskin: Math.min(8, odalar.reduce((t, o) => t + o.yetiskin, 0)), odalar: bookingOdaIstegi(odalar, odaAdlari ?? []) },
          c.signal,
        );
        if (c.signal.aborted) return;
        if (cevap.durum !== 'BEKLIYOR') { setPiyasa({ tur: 'bitti', cevap }); return; }
        await new Promise((r) => setTimeout(r, BEKLEME_MS));
        if (c.signal.aborted) return;
      }
      setPiyasa({ tur: 'hata', metin: T.bookingZamanAsimi });
    } catch {
      if (!c.signal.aborted) setPiyasa({ tur: 'hata', metin: T.bookingHata });
    }
  };

  const bitti = piyasa.tur === 'bitti' ? piyasa.cevap : null;
  const bookingFark = bitti?.durum === 'TAMAM' && sistemMinor !== null && para !== null && bitti.paraBirimi === para ? bitti.tutarMinor - sistemMinor : null;

  return (
    <div className="ht-satis-fiyati" data-testid={`${onek}-satis-fiyati-${slug ?? 'manuel'}`}>
      <span className="ht-etiket">{otelAdi}</span>
      {sistemMinor !== null && para !== null ? <small data-testid={`${onek}-sistem-fiyati`}>{T.sistemFiyati(paraYaz(sistemMinor, para))}</small> : null}
      <label>
        <span>{T.etiket}{para === null ? '' : ` (${para})`}</span>
        <input
          inputMode="decimal"
          value={deger}
          disabled={saltOkunur}
          placeholder={T.yerTutucu}
          aria-invalid={hatali}
          data-testid={`${onek}-satis-fiyati-giris-${slug ?? 'manuel'}`}
          onChange={(e) => degistir(e.target.value)}
        />
      </label>
      {deger !== '' && !saltOkunur ? <button type="button" className="ht-baglanti" onClick={() => degistir('')}>{T.sifirla}</button> : null}
      <small>{T.aciklama}</small>
      {hatali ? <small className="ht-hata-yazi" role="alert">{T.gecersiz}</small> : null}
      {gecerliMinor !== null && sistemMinor !== null && para !== null ? <small data-testid={`${onek}-gecerli-fiyat`}>{T.gecerli(paraYaz(gecerliMinor, para), farkMetni(gecerliMinor - sistemMinor, para))}</small> : null}
      <div className="ht-booking">
        <button type="button" className="ikincil" disabled={!cekilebilir || piyasa.tur === 'cekiliyor'} data-testid={`${onek}-booking-cek-${slug ?? 'manuel'}`} onClick={() => void cek()}>
          {piyasa.tur === 'bitti' ? T.bookingYenile : T.bookingCek}
        </button>
        {slug === null ? <small>{T.bookingSlugYok}</small> : null}
        {piyasa.tur === 'cekiliyor' ? <small role="status">{cokOda ? `${T.bookingCekiliyor}. ${T.bookingCokOdaSure}` : T.bookingCekiliyor}</small> : null}
        {piyasa.tur === 'hata' ? <small className="ht-hata-yazi" role="alert">{piyasa.metin}</small> : null}
        {bitti?.durum === 'YOK' ? <small data-testid={`${onek}-booking-yok`}>{T.bookingYok(bitti.sebep)}</small> : null}
        {bitti?.durum === 'TAMAM' ? (
          <div data-testid={`${onek}-booking-fiyat`}>
            <strong>{bitti.odalar !== undefined && bitti.odalar.length > 1 ? T.bookingToplam(paraYaz(bitti.tutarMinor, bitti.paraBirimi), bitti.odalar.length) : T.bookingFiyat(paraYaz(bitti.tutarMinor, bitti.paraBirimi))}</strong>
            {bookingFark !== null && para !== null ? <small> {T.bookingKarsilastirma(farkMetni(bookingFark, para))}</small> : null}
            {bitti.odalar !== undefined && bitti.odalar.length > 1 ? (
              <ul data-testid={`${onek}-booking-odalar`}>
                {bitti.odalar.map((o, i) => (
                  <li key={i}>{T.bookingOdaSatiri(i + 1, konukMetni({ yetiskin: o.yetiskin, cocukYaslari: o.cocukYaslari }), o.odaAdi, paraYaz(o.tutarMinor, bitti.paraBirimi))}</li>
                ))}
              </ul>
            ) : null}
            <small>{T.bookingNot}</small>
          </div>
        ) : null}
      </div>
    </div>
  );
}
